import { observer } from "mobx-react-lite";
import React, { useContext } from "react";
import {
  Redirect,
  Route,
  RouteComponentProps,
  Switch,
  useHistory,
  useRouteMatch
} from "react-router";
import {
  customerLanguageSelectionContext,
  EditLanguageSelectionSchema,
  languageSelectionContext,
  LanguageSelectionSchema,
  LanguageSelectionSchemaForForms
} from "./schema";
import {
  CreateView,
  IndexView,
  ShowView,
  withBreadCrumbs
} from "lego-gui-lib/lib";
import {
  CreateLanguageSelectionChoice,
  LanguageSelectionChoiceIndex,
  ShowLanguageSelectionChoice
} from "./languageSelectionChoice/views";
import { authStoreContext } from "../../core/mainStore";
import i18next from "i18next";
import { CustomerAwareRowButtons } from "../../common/CustomerAwareRowButtons";
import { ParentRoute } from "../../core/model/parent";
import { isAllowed } from "../../common/common";
import { useTranslation } from "react-i18next";
import { NestedView } from "../../common/nestedViewComponent";
import { authSchemaHelper } from "../../core/model/authSchemaHelper";

export const LanguageSelection = {
  url: "/languageSelections",
  handler: (props: RouteComponentProps<any>) => {
    const baseUrl = props.match.url;
    const title = i18next.t("hns:languageSelect:domain_plural");
    return withBreadCrumbs(
      { to: baseUrl, children: title },
      <>
        <ParentRoute
          dataSourceName={"languageSelection"}
          path={baseUrl + "/:id"}
        />
        <Switch>
          <Route exact={true} path={baseUrl} component={Index} />
          <Route
            path={baseUrl + "/:id/choices/create/:choiceId"}
            component={CreateLanguageSelectionChoice}
          />
          <Route
            path={baseUrl + "/:id/choices/create"}
            component={CreateLanguageSelectionChoice}
          />
          <Route
            path={baseUrl + "/:id/choices/:choiceId"}
            component={ShowLanguageSelectionChoice}
          />
          <Redirect from={baseUrl + "/:id/choices"} to={baseUrl + "/:id"} />
          <Route path={baseUrl + "/create"} component={Create} />
          <Route path={baseUrl + "/:id"} component={Show} />
        </Switch>
      </>
    );
  }
};

export const Index = observer(({ ...rest }: any) => {
  const auth = useContext(authStoreContext);
  const languageSelection = useContext(languageSelectionContext);
  const customerLanguageSelection = useContext(
    customerLanguageSelectionContext
  );

  const i18n = useTranslation();
  const title = i18n.t("hns:languageSelect:domain_plural", { count: 2 });

  return (
    <IndexView
      dataSource={
        auth.isAnyCustomerOwner()
          ? customerLanguageSelection
          : languageSelection.listDataSource
      }
      {...rest}
      title={title}
      toolbarButtons={{ delete: true, export: true }}
      rowButtons={(indexProps: any) =>
        CustomerAwareRowButtons(indexProps, { copy: false })
      }
      columns={LanguageSelectionSchema}
    />
  );
});

export const Create = observer(({ ...rest }: any) => {
  const languageSelection = useContext(languageSelectionContext);
  const match = useRouteMatch();
  const history = useHistory();

  const i18n = useTranslation();
  const title = i18n.t("common:create", {
    domain: "hns:languageSelect:domain"
  });

  return withBreadCrumbs(
    { to: match.url + "/edit", children: title },
    <CreateView
      dataSource={languageSelection.oneDataSource}
      {...rest}
      title={title}
      initialValues={authSchemaHelper.prepareInitialValuesForAnyCustomerOwnerCreate(
        languageSelection
      )}
      fields={LanguageSelectionSchemaForForms}
      id={match.params}
      goBack={() => history.goBack()}
    />
  );
});

export const Show = observer(({ ...rest }: any) => {
  const languageSelection = useContext(languageSelectionContext);
  const match = useRouteMatch();
  const history = useHistory();

  const allowed = isAllowed(languageSelection);
  const i18n = useTranslation();
  let title = allowed
    ? i18n.t("common:edit", { domain: "hns:languageSelect:domain" })
    : i18n.t("hns:languageSelect:domain");

  return withBreadCrumbs(
    { to: match.url + "/edit", children: title },
    <NestedView
      showView={
        <ShowView
          preloadData={false}
          dataSource={languageSelection.oneDataSource}
          hideDeleteButton={!allowed}
          title={title}
          fields={
            allowed
              ? EditLanguageSelectionSchema
              : EditLanguageSelectionSchema.makeAllReadonly()
          }
          id={match.params}
          onlyView={!allowed}
          goBack={() => history.goBack()}
        />
      }
      indexView={
        <LanguageSelectionChoiceIndex
          match={match}
          {...rest}
          isAllowed={allowed}
        />
      }
    />
  );
});
